import { useCallback, useEffect, useMemo, useState } from 'react';

interface Props {
  customerId: string;
  customerName?: string;
}

interface ApiResponse {
  status?: boolean;
  data?: unknown;
  message?: string;
}

interface ApiScheduleItem {
  scheduleIdx?: number;
  schedule_idx?: number;
  title?: string;
  startDt?: string;
  start_dt?: string;
  endDt?: string;
  end_dt?: string;
  type?: string;
  status?: string;
  memo?: string;
  customerIdx?: number;
  customer_idx?: number;
  customerId?: number;
  customer_id?: number;
  customer?: { customerIdx?: number; customer_idx?: number; customerId?: number; customer_id?: number };
  managerName?: string;
  manager_name?: string;
  assignedStaffIdx?: number;
  assigned_staff_idx?: number;
}

interface StaffItem {
  userIdx: number;
  name: string;
}

interface ApiStaffItem {
  userIdx?: number;
  name?: string;
}

interface CustomerSchedule {
  id: string;
  title: string;
  startDt: string;
  endDt: string;
  type: string;
  status: string;
  memo: string;
  managerName: string;
  assignedStaffIdx: number;
}

interface ScheduleFormState {
  date: string;
  time: string;
  endTime: string;
  type: string;
  title: string;
  memo: string;
  assignedStaffIdx: number;
}

const pad = (n: number) => String(n).padStart(2, '0');

const toDateParam = (date: Date) => date.toISOString().slice(0, 10);

const toLocalDateTime = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;

const buildStartDt = (date: string, time: string) => {
  const dt = new Date(`${date}T${time}:00`);
  if (Number.isNaN(dt.getTime())) return '';
  return toLocalDateTime(dt);
};

const buildEndDt = (date: string, time: string) => {
  const dt = new Date(`${date}T${time}:00`);
  if (Number.isNaN(dt.getTime())) return '';
  dt.setHours(dt.getHours() + 1);
  return toLocalDateTime(dt);
};

const parseJsonSafe = async (response: Response) => {
  try {
    return (await response.json()) as ApiResponse;
  } catch {
    return null;
  }
};

const formatDate = (dateTime: string) => {
  const d = new Date(dateTime);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const formatTime = (dateTime: string) => {
  const d = new Date(dateTime);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
};

const mapTypeLabel = (type: string) => {
  switch (type) {
    case 'WEDDING':
      return '본식';
    case 'REHEARSAL':
      return '리허설';
    case 'VENUE_VISIT':
      return '예식장 방문';
    case 'SDM':
      return '스드메';
    case 'ETC':
      return '기타';
    default:
      return type || '기타';
  }
};

const mapStatusLabel = (status: string) => {
  switch (status) {
    case 'SCHEDULED':
      return '예정';
    case 'COMPLETED':
      return '완료';
    case 'CANCELLED':
      return '취소';
    default:
      return status || '-';
  }
};

const getTypeBadgeClass = (type: string) => {
  switch (type) {
    case 'WEDDING':
      return 'bg-pink-100 text-pink-700';
    case 'REHEARSAL':
      return 'bg-indigo-100 text-indigo-700';
    case 'VENUE_VISIT':
      return 'bg-blue-100 text-blue-700';
    case 'SDM':
      return 'bg-purple-100 text-purple-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'COMPLETED':
      return 'bg-green-100 text-green-700';
    case 'CANCELLED':
      return 'bg-red-100 text-red-700';
    case 'SCHEDULED':
      return 'bg-yellow-100 text-yellow-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getDefaultForm = (assignedStaffIdx = 0): ScheduleFormState => {
  const now = new Date();
  const endHour = new Date(now.getTime() + 60 * 60 * 1000);
  return {
    date: toDateParam(now),
    time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
    endTime: `${pad(endHour.getHours())}:${pad(endHour.getMinutes())}`,
    type: 'WEDDING',
    title: '',
    memo: '',
    assignedStaffIdx
  };
};

const extractCustomerIdx = (item: ApiScheduleItem) =>
  Number(
    item.customerIdx ??
      item.customer_idx ??
      item.customerId ??
      item.customer_id ??
      item.customer?.customerIdx ??
      item.customer?.customer_idx ??
      item.customer?.customerId ??
      item.customer?.customer_id ??
      0
  );

const extractAssignedStaffIdx = (item: ApiScheduleItem) =>
  Number(item.assignedStaffIdx ?? item.assigned_staff_idx ?? 0);

export default function CustomerScheduleTab({ customerId, customerName }: Props) {
  const parsedCustomerId = Number(customerId);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [schedules, setSchedules] = useState<CustomerSchedule[]>([]);
  const [staffList, setStaffList] = useState<StaffItem[]>([]);

  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<CustomerSchedule | null>(null);
  const [scheduleForm, setScheduleForm] = useState<ScheduleFormState>(getDefaultForm());

  const fetchStaffList = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/user?status=ACTIVE&size=100', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await parseJsonSafe(response);
      const data = result?.data as { items?: ApiStaffItem[] } | undefined;
      const items = Array.isArray(data?.items) ? data.items : [];

      setStaffList(
        items
          .map((item) => ({
            userIdx: Number(item.userIdx ?? 0),
            name: item.name || '-'
          }))
          .filter((item) => item.userIdx > 0)
      );
    } catch (error) {
      console.error('Failed to fetch staff list:', error);
      setStaffList([]);
    }
  }, []);

  const fetchCustomerSchedules = useCallback(async () => {
    if (!Number.isFinite(parsedCustomerId)) {
      setSchedules([]);
      return;
    }

    setIsLoading(true);
    try {
      const fromDt = new Date();
      fromDt.setMonth(fromDt.getMonth() - 6);
      const toDt = new Date();
      toDt.setMonth(toDt.getMonth() + 12);

      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `https://api.wedmin.biz/schedule/calendar?fromDt=${toDateParam(fromDt)}&toDt=${toDateParam(toDt)}&category=CUSTOMER_EVENT`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = await parseJsonSafe(response);
      const items = Array.isArray(result?.data) ? (result.data as ApiScheduleItem[]) : [];

      const mapped = items
        .map((item, index) => {
          const itemCustomerIdx = extractCustomerIdx(item);
          const assignedStaffIdx = extractAssignedStaffIdx(item);
          return {
            id: String(item.scheduleIdx ?? item.schedule_idx ?? index),
            title: item.title || '',
            startDt: item.startDt || item.start_dt || '',
            endDt: item.endDt || item.end_dt || '',
            type: item.type || '',
            status: item.status || '',
            memo: item.memo || '',
            managerName: item.managerName || item.manager_name || '',
            customerIdx: itemCustomerIdx,
            assignedStaffIdx
          };
        })
        .filter((item) => item.customerIdx === parsedCustomerId)
        .sort((a, b) => new Date(a.startDt).getTime() - new Date(b.startDt).getTime())
        .map(({ customerIdx, ...rest }) => rest);

      setSchedules(mapped);
    } catch (error) {
      console.error('Failed to fetch customer schedules:', error);
      setSchedules([]);
    } finally {
      setIsLoading(false);
    }
  }, [parsedCustomerId]);

  useEffect(() => {
    fetchStaffList();
    fetchCustomerSchedules();
  }, [fetchStaffList, fetchCustomerSchedules]);

  const upcomingCount = useMemo(() => {
    const now = Date.now();
    return schedules.filter((schedule) => new Date(schedule.startDt).getTime() >= now).length;
  }, [schedules]);

  const resetForm = useCallback(
    (assignedStaffIdx = 0) => {
      setEditingSchedule(null);
      setScheduleForm(getDefaultForm(assignedStaffIdx));
    },
    []
  );

  const handleOpenCreate = () => {
    const defaultStaffIdx = staffList[0]?.userIdx ?? 0;
    resetForm(defaultStaffIdx);
    setShowScheduleForm(true);
  };

  const handleOpenEdit = (schedule: CustomerSchedule) => {
    const start = new Date(schedule.startDt);
    const end = schedule.endDt ? new Date(schedule.endDt) : new Date(start.getTime() + 60 * 60 * 1000);
    const date = Number.isNaN(start.getTime()) ? toDateParam(new Date()) : toDateParam(start);
    const time = Number.isNaN(start.getTime()) ? '10:00' : `${pad(start.getHours())}:${pad(start.getMinutes())}`;
    const endTime = Number.isNaN(end.getTime()) ? '11:00' : `${pad(end.getHours())}:${pad(end.getMinutes())}`;

    setEditingSchedule(schedule);
    setScheduleForm({
      date,
      time,
      endTime,
      type: schedule.type || 'WEDDING',
      title: schedule.title || '',
      memo: schedule.memo || '',
      assignedStaffIdx: schedule.assignedStaffIdx || 0
    });
    setShowScheduleForm(true);
  };

  const handleSaveSchedule = async () => {
    if (!Number.isFinite(parsedCustomerId)) {
      alert('고객 정보가 올바르지 않습니다.');
      return;
    }
    if (!scheduleForm.date || !scheduleForm.time) {
      alert('날짜와 시간을 입력해주세요.');
      return;
    }

    const startDt = buildStartDt(scheduleForm.date, scheduleForm.time);
    const endDt = scheduleForm.endTime
      ? buildStartDt(scheduleForm.date, scheduleForm.endTime)
      : buildEndDt(scheduleForm.date, scheduleForm.time);
    if (!startDt || !endDt) {
      alert('날짜/시간 형식을 확인해주세요.');
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const payload = {
        category: 'CUSTOMER_EVENT',
        type: scheduleForm.type,
        title: scheduleForm.title.trim() || `${mapTypeLabel(scheduleForm.type)} 일정`,
        customerName: (customerName || '').trim(),
        startDt,
        endDt,
        customerIdx: parsedCustomerId,
        assignedStaffIdx: Number(scheduleForm.assignedStaffIdx || 0),
        roomIdx: 0,
        memo: scheduleForm.memo.trim()
      };

      const url = editingSchedule
        ? `https://api.wedmin.biz/schedule/${editingSchedule.id}`
        : 'https://api.wedmin.biz/schedule';
      const method = editingSchedule ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await parseJsonSafe(response);
      if (!response.ok || result?.status === false) {
        throw new Error(result?.message || '일정 저장에 실패했습니다.');
      }

      alert(editingSchedule ? '일정이 수정되었습니다.' : '일정이 등록되었습니다.');
      setShowScheduleForm(false);
      resetForm(staffList[0]?.userIdx ?? 0);
      fetchCustomerSchedules();
    } catch (error: unknown) {
      console.error('Save schedule error:', error);
      const message = error instanceof Error ? error.message : '일정 저장 중 오류가 발생했습니다.';
      alert(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!window.confirm('이 일정을 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/schedule/${scheduleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await parseJsonSafe(response);

      if (!response.ok || result?.status === false) {
        throw new Error(result?.message || '삭제에 실패했습니다.');
      }

      alert('일정이 삭제되었습니다.');
      if (editingSchedule?.id === scheduleId) {
        setShowScheduleForm(false);
        resetForm(staffList[0]?.userIdx ?? 0);
      }
      fetchCustomerSchedules();
    } catch (error: unknown) {
      console.error('Delete schedule error:', error);
      const message = error instanceof Error ? error.message : '일정 삭제 중 오류가 발생했습니다.';
      alert(message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">고객 일정</h3>
            <p className="text-sm text-gray-500 mt-1">해당 고객의 일정 등록, 수정, 삭제를 진행할 수 있습니다.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 mr-1">총 {schedules.length}건 / 예정 {upcomingCount}건</span>
            <button
              onClick={fetchCustomerSchedules}
              disabled={isLoading}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60 cursor-pointer"
            >
              <i className="ri-refresh-line mr-1"></i>
              새로고침
            </button>
            <button
              onClick={handleOpenCreate}
              className="px-3 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 cursor-pointer"
            >
              <i className="ri-add-line mr-1"></i>
              일정 등록
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500">일정을 불러오는 중입니다...</div>
        ) : schedules.length === 0 ? (
          <div className="p-8 text-center text-gray-500">등록된 고객 일정이 없습니다.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {schedules.map((schedule) => (
              <div key={schedule.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span
                        className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${getTypeBadgeClass(schedule.type)}`}
                      >
                        {mapTypeLabel(schedule.type)}
                      </span>
                      <span
                        className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(schedule.status)}`}
                      >
                        {mapStatusLabel(schedule.status)}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{schedule.title || mapTypeLabel(schedule.type)}</p>
                    <p className="text-sm text-gray-600 mt-1">담당자: {schedule.managerName || '미지정'}</p>
                    {schedule.memo && (
                      <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{schedule.memo}</p>
                    )}
                  </div>
                  <div className="text-right text-sm text-gray-700 shrink-0">
                    <p className="font-medium">{formatDate(schedule.startDt)}</p>
                    <p>
                      {formatTime(schedule.startDt)} - {formatTime(schedule.endDt)}
                    </p>
                    <div className="flex items-center justify-end gap-2 mt-3">
                      <button
                        onClick={() => handleOpenEdit(schedule)}
                        className="px-2.5 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 cursor-pointer"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDeleteSchedule(schedule.id)}
                        className="px-2.5 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 cursor-pointer"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showScheduleForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-lg font-semibold text-gray-900">{editingSchedule ? '일정 수정' : '일정 등록'}</h4>
              <button
                onClick={() => {
                  setShowScheduleForm(false);
                  resetForm(staffList[0]?.userIdx ?? 0);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">날짜</label>
                  <input
                    type="date"
                    value={scheduleForm.date}
                    onChange={(e) => setScheduleForm((prev) => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">시작 시간</label>
                  <input
                    type="time"
                    value={scheduleForm.time}
                    onChange={(e) => {
                      const startTime = e.target.value;
                      // 종료시간 자동 +1시간
                      const [h, m] = startTime.split(':').map(Number);
                      const autoEnd = `${pad((h + 1) % 24)}:${pad(m)}`;
                      setScheduleForm((prev) => ({ ...prev, time: startTime, endTime: prev.endTime || autoEnd }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">종료 시간</label>
                  <input
                    type="time"
                    value={scheduleForm.endTime}
                    onChange={(e) => setScheduleForm((prev) => ({ ...prev, endTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">일정 유형</label>
                  <select
                    value={scheduleForm.type}
                    onChange={(e) => setScheduleForm((prev) => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="WEDDING">본식</option>
                    <option value="REHEARSAL">리허설</option>
                    <option value="VENUE_VISIT">예식장 방문</option>
                    <option value="SDM">스드메</option>
                    <option value="ETC">기타</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">담당자</label>
                  <select
                    value={scheduleForm.assignedStaffIdx}
                    onChange={(e) =>
                      setScheduleForm((prev) => ({ ...prev, assignedStaffIdx: Number(e.target.value) }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value={0}>미지정</option>
                    {staffList.map((staff) => (
                      <option key={staff.userIdx} value={staff.userIdx}>
                        {staff.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">일정 제목</label>
                <input
                  type="text"
                  value={scheduleForm.title}
                  onChange={(e) => setScheduleForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="비워두면 유형 기반으로 자동 입력됩니다."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">메모</label>
                <textarea
                  rows={4}
                  value={scheduleForm.memo}
                  onChange={(e) => setScheduleForm((prev) => ({ ...prev, memo: e.target.value }))}
                  placeholder="일정 관련 메모를 입력하세요."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-2">
              {editingSchedule && (
                <button
                  onClick={() => handleDeleteSchedule(editingSchedule.id)}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                >
                  삭제
                </button>
              )}
              <button
                onClick={() => {
                  setShowScheduleForm(false);
                  resetForm(staffList[0]?.userIdx ?? 0);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleSaveSchedule}
                disabled={isSaving}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-60"
              >
                {isSaving ? '저장 중...' : editingSchedule ? '수정' : '등록'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
